import { Dialog } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCircleCheck } from "react-icons/tb";
import { ModalCloseButton } from "../../../Components/Molecules/ValutDialog";
import { PasswordConfirm } from "../../../Components/Molecules";
import { confirmPixRefund } from "../../../Hooks/UsePix";
import { formatInputValue } from "../../../Utils/formatInputValue";
import { convertToCents, formatCurrency } from "../../../Utils/formatters";
import { getExtractTransactionAmountCents } from "../Extract/extractFilters";
import { getPixTransactionEndToEndId } from "./pixStatementFilters";

const formatAmountInputFromCents = (cents) =>
  formatInputValue(String(Math.max(0, Math.round(Number(cents) || 0))));

const STEPS = ["amount", "security", "success"];

const stepIndex = (step) => Math.max(0, STEPS.indexOf(step));

export const PixRefundModal = ({
  open,
  item,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState("amount");
  const [amountMode, setAmountMode] = useState("total");
  const [amountInput, setAmountInput] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refundedAmountCents, setRefundedAmountCents] = useState(0);

  const maxAmountCents = useMemo(
    () => (item ? getExtractTransactionAmountCents(item) : 0),
    [item],
  );

  const refundAll = amountMode === "total";

  const refundAmountCents = useMemo(() => {
    if (refundAll) return maxAmountCents;
    return convertToCents(amountInput || "0");
  }, [amountInput, maxAmountCents, refundAll]);

  const amountError = useMemo(() => {
    if (refundAll) return null;
    if (!amountInput) return intl.get("pix.refund.amountRequired");
    if (refundAmountCents <= 0) return intl.get("pix.refund.amountRequired");
    if (refundAmountCents > maxAmountCents) {
      return intl.get("pix.refund.amountExceedsMax", {
        max: formatCurrency(maxAmountCents),
      });
    }
    return null;
  }, [amountInput, maxAmountCents, refundAll, refundAmountCents]);

  useEffect(() => {
    if (!open) {
      setStep("amount");
      setAmountMode("total");
      setAmountInput("");
      setPasswordError(null);
      setLoading(false);
      setRefundedAmountCents(0);
      return;
    }

    setAmountInput(formatAmountInputFromCents(maxAmountCents));
    setAmountMode("total");
    setStep("amount");
    setPasswordError(null);
    setLoading(false);
    setRefundedAmountCents(0);
  }, [open, item, maxAmountCents]);

  const handleSelectTotal = useCallback(() => {
    setAmountMode("total");
    setAmountInput(formatAmountInputFromCents(maxAmountCents));
  }, [maxAmountCents]);

  const handleSelectDefined = useCallback(() => {
    setAmountMode("defined");
    if (!amountInput) {
      setAmountInput(formatAmountInputFromCents(maxAmountCents));
    }
  }, [amountInput, maxAmountCents]);

  const handleAmountChange = useCallback((event) => {
    setAmountMode("defined");
    setAmountInput(formatInputValue(event.target.value));
  }, []);

  const handleProceedToPassword = useCallback(() => {
    if (amountError) return;
    if (refundAmountCents <= 0 || refundAmountCents > maxAmountCents) return;
    setPasswordError(null);
    setStep("security");
  }, [amountError, maxAmountCents, refundAmountCents]);

  const handleSecurityBack = useCallback(() => {
    if (loading) return;
    setPasswordError(null);
    setStep("amount");
  }, [loading]);

  const handleSubmitPassword = useCallback(
    async (passwordCombination) => {
      if (!item || loading) return;

      setLoading(true);
      setPasswordError(null);

      const result = await confirmPixRefund({
        operation_id: item.operation_id,
        pix_id: item.pix_id ?? item.id,
        end_to_end_id: getPixTransactionEndToEndId(item),
        amount: refundAmountCents,
        password_combination: passwordCombination,
      });

      setLoading(false);

      if (result?.success) {
        setRefundedAmountCents(refundAmountCents);
        setStep("success");
        return;
      }

      setPasswordError({
        title: result?.message || intl.get("passwordConfirm.warning"),
        description: result?.description || intl.get("passwordConfirm.invalidTransactionPassword"),
      });
    },
    [item, loading, refundAmountCents],
  );

  const handleFinish = useCallback(() => {
    onSuccess?.();
    onClose?.();
  }, [onClose, onSuccess]);

  const handleDialogClose = (_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    if (loading) return;
    if (step === "success") {
      handleFinish();
      return;
    }
    onClose?.();
  };

  const handleCloseClick = () => {
    if (loading) return;
    if (step === "success") {
      handleFinish();
      return;
    }
    onClose?.();
  };

  if (!item) return null;

  const currentStepIndex = stepIndex(step);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: "!rounded-2xl !shadow-card overflow-hidden" }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <ModalCloseButton
          onClick={handleCloseClick}
          disabled={loading}
          className="absolute right-3 top-3 z-10"
        />

        {step !== "security" ? (
          <div className="px-5 pb-2 pt-6 sm:px-6">
            <h2 className="pr-8 text-center text-lg font-bold text-brand-dark dark:text-gray-100">
              {step === "success"
                ? intl.get("pix.refund.successTitle")
                : intl.get("pix.refund.title")}
            </h2>
            {step === "amount" ? (
              <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
                {intl.get("pix.refund.subtitle", {
                  amount: formatCurrency(maxAmountCents),
                })}
              </p>
            ) : null}

            <ol className="mt-4 flex items-center justify-center gap-2" aria-label="steps">
              {STEPS.map((key, index) => {
                const active = index === currentStepIndex;
                const done = index < currentStepIndex;
                return (
                  <li
                    key={key}
                    className={`h-1.5 w-8 rounded-full transition ${
                      active || done ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-brand-dark-border"
                    }`}
                    aria-current={active ? "step" : undefined}
                  />
                );
              })}
            </ol>
          </div>
        ) : null}

        {step === "security" ? (
          <PasswordConfirm
            variant="embedded"
            open
            submitPassword={handleSubmitPassword}
            onBack={handleSecurityBack}
            onClose={handleCloseClick}
            error={passwordError}
            loading={loading}
            onDismissError={() => setPasswordError(null)}
          />
        ) : null}

        {step === "amount" ? (
          <>
            <div className="px-5 pb-2 sm:px-6">
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-brand-dark-border dark:bg-brand-dark-surface">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("pix.refund.originalTransaction")}
                </p>
                <p className="mt-1 text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {item.description || "—"}
                </p>
                {getPixTransactionEndToEndId(item) ? (
                  <p className="mt-2 break-all font-mono text-xs text-gray-500 dark:text-gray-400">
                    {getPixTransactionEndToEndId(item)}
                  </p>
                ) : null}
              </div>

              <div className="mt-5 space-y-3">
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {intl.get("pix.refund.amountModeLabel")}
                </p>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleSelectTotal}
                    className={`rounded-xl border px-3 py-3 text-left transition ${
                      amountMode === "total"
                        ? "border-[#e7a73f] bg-brand-cream text-brand-dark"
                        : "border-gray-200 bg-white text-gray-600 hover:border-[#e7a73f]/40 hover:bg-brand-cream/60 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-semibold">
                      {intl.get("pix.refund.modeTotal")}
                    </span>
                    <span className="mt-1 block text-xs opacity-80">
                      {formatCurrency(maxAmountCents)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectDefined}
                    className={`rounded-xl border px-3 py-3 text-left transition ${
                      amountMode === "defined"
                        ? "border-[#e7a73f] bg-brand-cream text-brand-dark"
                        : "border-gray-200 bg-white text-gray-600 hover:border-[#e7a73f]/40 hover:bg-brand-cream/60 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-semibold">
                      {intl.get("pix.refund.modeDefined")}
                    </span>
                    <span className="mt-1 block text-xs opacity-80">
                      {intl.get("pix.refund.modeDefinedHint")}
                    </span>
                  </button>
                </div>

                {amountMode === "defined" ? (
                  <div className="space-y-2 pt-1">
                    <label
                      htmlFor="pix-refund-amount"
                      className="text-sm font-semibold text-brand-dark dark:text-gray-100"
                    >
                      {intl.get("pix.refund.amountLabel")}
                    </label>
                    <div
                      className={`pix-transfer-amount-field ${amountError ? "pix-transfer-amount-field--error" : ""}`}
                    >
                      <span className="pix-transfer-amount-field__prefix">R$</span>
                      <input
                        id="pix-refund-amount"
                        type="text"
                        value={amountInput}
                        onChange={handleAmountChange}
                        inputMode="numeric"
                        maxLength={14}
                        placeholder="0,00"
                        aria-invalid={Boolean(amountError)}
                        className="pix-transfer-amount-field__input"
                      />
                    </div>
                    {amountError ? (
                      <p className="text-xs text-red-600">{amountError}</p>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {intl.get("pix.refund.maxAmount", {
                          max: formatCurrency(maxAmountCents),
                        })}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-auto border-t border-gray-100 px-5 py-4 sm:px-6 dark:border-brand-dark-border">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="btn-primary w-full sm:w-auto"
                  onClick={handleProceedToPassword}
                  disabled={Boolean(amountError) || refundAmountCents <= 0}
                >
                  {intl.get("pix.refund.continue")}
                </button>
              </div>
            </div>
          </>
        ) : null}

        {step === "success" ? (
          <>
            <div className="flex flex-col items-center px-5 py-6 text-center sm:px-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
                <TbCircleCheck className="h-8 w-8" aria-hidden />
              </span>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                {intl.get("pix.refund.successDescription", {
                  amount: formatCurrency(refundedAmountCents || refundAmountCents),
                })}
              </p>
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                {intl.get("pix.refund.successHint")}
              </p>
            </div>
            <div className="border-t border-gray-100 px-5 py-4 sm:px-6 dark:border-brand-dark-border">
              <div className="flex justify-end">
                <button type="button" className="btn-primary w-full sm:w-auto" onClick={handleFinish}>
                  {intl.get("pix.refund.finish")}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
};
